/**
 * The four small dialogs the grid needs, kept out of the grid itself.
 *
 * Times are the one thing a manager types. The SAR instruction is explicit:
 * enter the officer's ACTUAL clock-in, not the scheduled start, so the worked
 * times are editable on every cell while the planned window stays read-only
 * for variance. Hours are never typed - the server computes them from the
 * times, every time.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const hhmm = (v) => (v || '').slice(0, 5);

export function TimesDialog({ open, onOpenChange, initial, scopeLabel, onApply }) {
  const [timeIn, setTimeIn] = useState('');
  const [timeOut, setTimeOut] = useState('');
  const [nextDay, setNextDay] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTimeIn(hhmm(initial?.time_in) || '');
    setTimeOut(hhmm(initial?.time_out) || '');
    setNextDay(Boolean(initial?.next_day));
  }, [open, initial]);

  // Crossing midnight is derived, not a preference: 19:00-07:00 is overnight
  // whatever the box says. The switch only exists for a shift that runs a full
  // day or more, which is rare and real.
  useEffect(() => {
    if (timeIn && timeOut && timeOut <= timeIn) setNextDay(true);
  }, [timeIn, timeOut]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="times-dialog">
        <DialogHeader>
          <DialogTitle>Times</DialogTitle>
          <DialogDescription>
            {scopeLabel} · enter the officer's actual clock-in and clock-out.
            Hours are worked out for you.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-fg-muted">In</Label>
            <Input type="time" value={timeIn} onChange={(e) => setTimeIn(e.target.value)}
              data-testid="times-dialog-in" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs text-fg-muted">Out</Label>
            <Input type="time" value={timeOut} onChange={(e) => setTimeOut(e.target.value)}
              data-testid="times-dialog-out" className="mt-1" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-line
                        bg-surface-sunken px-3 py-2">
          <span className="text-sm text-fg">Runs into the next day</span>
          <Switch checked={nextDay} onCheckedChange={setNextDay}
            data-testid="times-dialog-next-day" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!timeIn || !timeOut} data-testid="times-dialog-apply"
            onClick={() => { onApply({ time_in: timeIn, time_out: timeOut, next_day: nextDay }); onOpenChange(false); }}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MoveDialog({ open, onOpenChange, posts, dates, count, onApply }) {
  const [postId, setPostId] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => { if (open) { setPostId(''); setDate(''); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle>Move {count} shift{count === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Change the post, the day, or both. Anything you leave alone stays as it is.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-fg-muted">Post</Label>
            <Select value={postId} onValueChange={setPostId}>
              <SelectTrigger data-testid="move-dialog-post" className="mt-1">
                <SelectValue placeholder="Keep the post it is on" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {posts.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.post_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-fg-muted">Day</Label>
            <Select value={date} onValueChange={setDate}>
              <SelectTrigger data-testid="move-dialog-date" className="mt-1">
                <SelectValue placeholder="Keep the day it is on" />
              </SelectTrigger>
              <SelectContent>
                {dates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!postId && !date} data-testid="move-dialog-apply"
            onClick={() => {
              const fields = {};
              if (postId) fields.job_post_id = postId;
              if (date) fields.shift_date = date;
              onApply(fields);
              onOpenChange(false);
            }}>
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HoursTypeDialog({ open, onOpenChange, hoursTypes, count, onApply }) {
  const [hoursTypeId, setHoursTypeId] = useState('');
  const [billable, setBillable] = useState('keep');

  useEffect(() => { if (open) { setHoursTypeId(''); setBillable('keep'); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="hours-type-dialog">
        <DialogHeader>
          <DialogTitle>{count} shift{count === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Change the hours type or whether the hours are billable. Billable says
            whether the client can be charged - it shows no rate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-fg-muted">Hours type</Label>
            <Select value={hoursTypeId} onValueChange={setHoursTypeId}>
              <SelectTrigger data-testid="hours-type-dialog-select" className="mt-1">
                <SelectValue placeholder="Leave as it is" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {hoursTypes.map((h) => (
                  <SelectItem key={h.id} value={h.id}>{h.name || h.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-fg-muted">Billable</Label>
            <Select value={billable} onValueChange={setBillable}>
              <SelectTrigger data-testid="hours-type-dialog-billable" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keep">Leave as it is</SelectItem>
                <SelectItem value="yes">Billable</SelectItem>
                <SelectItem value="no">Not billable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!hoursTypeId && billable === 'keep'}
            data-testid="hours-type-dialog-apply"
            onClick={() => {
              const fields = {};
              if (hoursTypeId) fields.hours_type_id = hoursTypeId;
              if (billable !== 'keep') fields.non_billable = billable === 'no';
              onApply(fields);
              onOpenChange(false);
            }}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OpenPostDialog({ open, onOpenChange, onApply }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="open-post-dialog">
        <DialogHeader>
          <DialogTitle>Leave this post open</DialogTitle>
          <DialogDescription>
            An open post needs a reason, so the gap is explainable later.
          </DialogDescription>
        </DialogHeader>
        <Input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="No officer available" data-testid="open-post-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!reason.trim()} data-testid="open-post-dialog-apply"
            onClick={() => { onApply(reason.trim()); onOpenChange(false); }}>
            Leave open
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
